// packages/ui-kit/src/primitives/Button.tsx
// C3F v18 — reference: design/system/components/core/Button.{jsx,prompt.md}
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";

type Variant = "primary" | "accent" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";
type Shape = "pill" | "rounded";

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  variant?: Variant;
  size?: Size;
  /** Radius follows ROLE: pill for primary/accent (they close an intention),
   *  12px for the rest (they sit next to fields). Override only with reason. */
  shape?: Shape;
  loading?: boolean;
  /** On surfaces fixed to Night in BOTH themes (login panel, first-run welcome,
   *  full-Night bands). Without it, `primary` fills with --fg-default — which in
   *  Paper theme IS the Night ink: fill identical to the surface, 1:1. On
   *  surfaces that follow the theme the tokens already do the work: omit it. */
  onDark?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

const variantCls: Record<Variant, string> = {
  primary: "bg-fg-default text-fg-inverse hover:bg-fg-strong active:bg-fg-strong",
  // Accent = Spectrum-1 as a solid fill: at most one per screen, never for
  // repeated actions. White text on top (5.35:1) — as TEXT accent needs
  // `text-accent-text`, not this fill.
  accent: "bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-hover",
  // Outline-only control → --border-control (WCAG 1.4.11), not --border-default.
  secondary: "border border-border-control text-fg-default hover:bg-hover active:bg-active",
  ghost: "text-fg-default hover:bg-hover active:bg-active",
  danger: "bg-danger text-white hover:brightness-[1.08]",
};

const onDarkCls: Record<Variant, string> = {
  primary: "bg-praesto-paper text-praesto-night hover:bg-white active:bg-white",
  accent: "bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-hover",
  secondary:
    "border border-[rgba(244,240,233,.28)] text-praesto-paper hover:bg-[rgba(244,240,233,.10)]",
  ghost: "text-praesto-paper hover:bg-[rgba(244,240,233,.10)]",
  danger: "bg-danger text-white hover:brightness-[1.08]",
};

// DS sizes: sm 34 · md 44 · lg 52. On touch viewports sm keeps the 44px floor
// (staff taps with gloves) and drops to the dense 34px at the sm: breakpoint.
const sizeCls: Record<Size, string> = {
  sm: "min-h-touch sm:min-h-[34px] px-3.5 text-caption gap-1.5",
  md: "min-h-touch px-5 text-sm gap-2",
  lg: "min-h-[52px] px-[26px] text-base gap-2.5",
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = "secondary",
    size = "md",
    shape,
    loading = false,
    onDark = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    disabled,
    className = "",
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  const resolvedShape: Shape =
    shape ?? (variant === "primary" || variant === "accent" ? "pill" : "rounded");
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center whitespace-nowrap leading-none font-sans font-semibold tracking-body transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-45 disabled:cursor-not-allowed ${
        resolvedShape === "pill" ? "rounded-pill" : "rounded-md"
      } ${(onDark ? onDarkCls : variantCls)[variant]} ${sizeCls[size]} ${fullWidth ? "w-full" : ""} ${className}`}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="inline-block h-3.5 w-3.5 rounded-full border-2 border-current border-r-transparent [animation:px-spin_.7s_linear_infinite]"
        />
      ) : (
        iconLeft
      )}
      {children}
      {iconRight}
    </button>
  );
});
